/**
 * Species catalogue data layer.
 *
 * Joins the four static exports in this folder into one typed list that the catalogue
 * charts, cards and filters all read from:
 *   - species.json            the catalogue species (canonical names, drives the card grid)
 *   - Species_summary.json    spread class, daily occurrence, rank, peak time per species
 *   - Species_deployment.json avg. daily occurrence of each species in each deployment
 *   - daily_pattern.json      combined occurrences per 30-minute slot per species
 *
 * Every derived number (ranks, shares, counts, headline stats) is computed over the
 * species.json list, so the charts above the grid always agree with the cards in it.
 */
import speciesList from './species.json';
import summaryJson from './Species_summary.json';
import deploymentJson from './Species_deployment.json';
import patternJson from './daily_pattern.json';

// ── Raw file shapes ──────────────────────────────────────────────────────────

interface RawSummary {
  species: string;
  spread_class: string;
  deployments_detected: number;
  occupancy: number;
  average_daily_occurrence_combined: number | null;
  daily_occurrence_rank: number | null;
  share_of_combined_daily_occurrence: number | null;
  deployments_with_activity_data: number;
  peak_time_slot_start: string | null;
  peak_band: string | null;
  top_pairing_species: string | null;
}

interface RawDeploymentFile {
  deployment_count: number;
  activity_data_by_deployment: Record<string, string>;
  deployments: Array<{ Deployment: string; 'Site name'?: string }>;
  species_deployment: Array<{
    species: string;
    /** number = measured avg. daily occurrence, 'n/d' = detected but no activity data, null = not detected */
    average_daily_occurrence_by_deployment: Record<string, number | string | null>;
  }>;
}

interface RawPatternFile {
  slot_interval_minutes: number;
  species: Array<{ species: string; occurrences_by_slot: Record<string, number>; total: number }>;
}

const summaries = (summaryJson as unknown as { species: RawSummary[] }).species;
const deploymentFile = deploymentJson as unknown as RawDeploymentFile;
const patternFile = patternJson as unknown as RawPatternFile;

// ── Public types ─────────────────────────────────────────────────────────────

export type SpreadClassId = 'ubiquitous' | 'common' | 'occasional' | 'rare';

export type DeploymentSpreadStatus = 'measured' | 'no-activity-data' | 'not-detected';

export interface DeploymentSpreadCell {
  deployment: string;
  status: DeploymentSpreadStatus;
  /** Avg. daily occurrence in this deployment; only set when status is 'measured'. */
  value: number | null;
}

export interface SpeciesCatalogEntry {
  name: string;
  type: string;
  spreadClass: SpreadClassId;
  deploymentsDetected: number;
  occupancy: number;
  /** Avg. daily occurrence combined across deployments with activity data. */
  dailyOccurrence: number | null;
  /** 1-based rank by daily occurrence within the catalogue; null when there is no activity data. */
  rank: number | null;
  /** Share of the catalogue's combined daily occurrence (0–1). */
  share: number | null;
  deploymentsWithActivityData: number;
  peakTime: string | null;
  peakBand: string | null;
  topPairingSpecies: string | null;
  /** Combined occurrences per slot, aligned with TIME_SLOTS. */
  pattern: number[];
  patternMax: number;
  /** One cell per deployment, in deployment order. */
  spread: DeploymentSpreadCell[];
}

export interface PeakBandOption {
  band: string;
  /** Earliest and latest peak slot observed in this band. */
  from: string;
  to: string;
  count: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const SPREAD_CLASS_IDS: SpreadClassId[] = ['ubiquitous', 'common', 'occasional', 'rare'];

const toSpreadClassId = (raw: string): SpreadClassId => {
  const id = raw.trim().toLowerCase() as SpreadClassId;
  return SPREAD_CLASS_IDS.includes(id) ? id : 'rare';
};

const toMinutes = (slot: string): number => {
  const [h, m] = slot.split(':').map(Number);
  return h * 60 + m;
};

const fromMinutes = (total: number): string => {
  const wrapped = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const SLOT_INTERVAL_MINUTES = patternFile.slot_interval_minutes || 30;

/** End of the slot that starts at `slot`, e.g. "10:00" -> "10:30". */
export const slotEnd = (slot: string): string => fromMinutes(toMinutes(slot) + SLOT_INTERVAL_MINUTES);

// ── Static axes ──────────────────────────────────────────────────────────────

export const TIME_SLOTS: string[] = Array.from(
  { length: Math.round(1440 / SLOT_INTERVAL_MINUTES) },
  (_, i) => fromMinutes(i * SLOT_INTERVAL_MINUTES),
);

export const DEPLOYMENTS: string[] = deploymentFile.deployments.map((d) => d.Deployment);

export const DEPLOYMENT_COUNT = deploymentFile.deployment_count || DEPLOYMENTS.length;

export const DEPLOYMENTS_WITH_ACTIVITY_DATA = Object.values(
  deploymentFile.activity_data_by_deployment,
).filter((v) => v === 'yes').length;

// ── Catalogue ────────────────────────────────────────────────────────────────

const summaryByName = new Map(summaries.map((s) => [s.species, s]));
const patternByName = new Map(patternFile.species.map((p) => [p.species, p.occurrences_by_slot]));
const deploymentByName = new Map(
  deploymentFile.species_deployment.map((d) => [d.species, d.average_daily_occurrence_by_deployment]),
);

const buildSpread = (name: string): DeploymentSpreadCell[] => {
  const byDeployment = deploymentByName.get(name) || {};
  return DEPLOYMENTS.map((deployment) => {
    const raw = byDeployment[deployment];
    if (typeof raw === 'number') return { deployment, status: 'measured', value: raw };
    if (raw === null || raw === undefined) return { deployment, status: 'not-detected', value: null };
    return { deployment, status: 'no-activity-data', value: null };
  });
};

const unranked: Omit<SpeciesCatalogEntry, 'rank' | 'share'>[] = (speciesList as string[])
  .map((rawName) => rawName.trim())
  .filter((name) => {
    if (summaryByName.has(name)) return true;
    console.warn(`[species catalogue] "${name}" is in species.json but missing from Species_summary.json`);
    return false;
  })
  .map((name) => {
    const s = summaryByName.get(name) as RawSummary;
    const slots = patternByName.get(name) || {};
    const pattern = TIME_SLOTS.map((slot) => slots[slot] ?? 0);
    return {
      name,
      // Every species in the current export is a bird; no taxon column exists yet.
      type: 'Avian',
      spreadClass: toSpreadClassId(s.spread_class),
      deploymentsDetected: s.deployments_detected,
      occupancy: s.occupancy,
      dailyOccurrence: s.average_daily_occurrence_combined,
      deploymentsWithActivityData: s.deployments_with_activity_data,
      peakTime: s.peak_time_slot_start,
      peakBand: s.peak_band,
      topPairingSpecies: s.top_pairing_species,
      pattern,
      patternMax: Math.max(0, ...pattern),
      spread: buildSpread(name),
    };
  });

export const COMBINED_DAILY_OCCURRENCE = unranked.reduce((acc, s) => acc + (s.dailyOccurrence ?? 0), 0);

/** Highest daily occurrence first; species without activity data keep alphabetical order at the end. */
const byOccurrenceDesc = (a: { dailyOccurrence: number | null; name: string }, b: typeof a) =>
  (b.dailyOccurrence ?? -1) - (a.dailyOccurrence ?? -1) || a.name.localeCompare(b.name);

const rankByName = new Map(
  unranked
    .filter((s) => s.dailyOccurrence !== null)
    .sort(byOccurrenceDesc)
    .map((s, i) => [s.name, i + 1]),
);

export const SPECIES_CATALOG: SpeciesCatalogEntry[] = unranked
  .map((s) => ({
    ...s,
    rank: rankByName.get(s.name) ?? null,
    share:
      s.dailyOccurrence !== null && COMBINED_DAILY_OCCURRENCE > 0
        ? s.dailyOccurrence / COMBINED_DAILY_OCCURRENCE
        : null,
  }))
  .sort(byOccurrenceDesc);

export const RANKED_SPECIES_COUNT = rankByName.size;

export const MAX_DAILY_OCCURRENCE = Math.max(1, ...SPECIES_CATALOG.map((s) => s.dailyOccurrence ?? 0));

export const SPREAD_CLASS_COUNTS: Record<SpreadClassId, number> = SPECIES_CATALOG.reduce(
  (acc, s) => ({ ...acc, [s.spreadClass]: acc[s.spreadClass] + 1 }),
  { ubiquitous: 0, common: 0, occasional: 0, rare: 0 } as Record<SpreadClassId, number>,
);

/** Peak bands present in the data, earliest first, with the slot range each one covers. */
export const PEAK_BANDS: PeakBandOption[] = Array.from(
  SPECIES_CATALOG.reduce((acc, s) => {
    if (!s.peakBand || !s.peakTime) return acc;
    const prev = acc.get(s.peakBand);
    const t = toMinutes(s.peakTime);
    acc.set(s.peakBand, {
      band: s.peakBand,
      from: prev && toMinutes(prev.from) <= t ? prev.from : s.peakTime,
      to: prev && toMinutes(prev.to) >= t ? prev.to : s.peakTime,
      count: (prev?.count ?? 0) + 1,
    });
    return acc;
  }, new Map<string, PeakBandOption>()).values(),
).sort((a, b) => toMinutes(a.from) - toMinutes(b.from));

export const NO_PEAK_COUNT = SPECIES_CATALOG.filter((s) => !s.peakBand).length;

/** Slot with the highest combined occurrence across every catalogue species. */
export const BUSIEST_SLOT = (() => {
  const totals = TIME_SLOTS.map((_, i) => SPECIES_CATALOG.reduce((acc, s) => acc + s.pattern[i], 0));
  let best = 0;
  totals.forEach((v, i) => {
    if (v > totals[best]) best = i;
  });
  return { start: TIME_SLOTS[best], end: slotEnd(TIME_SLOTS[best]), total: totals[best] };
})();

/** Top species by combined avg. daily occurrence, for the "most frequently detected" chart. */
export const topSpeciesByOccurrence = (limit = 10): Array<{ name: string; count: number }> =>
  SPECIES_CATALOG.filter((s) => s.dailyOccurrence !== null)
    .slice(0, limit)
    .map((s) => ({ name: s.name, count: s.dailyOccurrence as number }));
